import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BlogLayout from "./BlogLayout";
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  ArrowRight,
  Zap,
  Shield,
  Banknote,
  Users,
  ShoppingCart,
  Code,
  Layers,
} from "lucide-react";

const OripaSiteComparisonPage: React.FC = () => {
  // 比較データ（3つの方法）
  const comparisonData = [
    {
      name: "ECプラットフォーム",
      icon: ShoppingCart,
      type: "既存サービス活用",
      example: "BASE、STORES、Shopifyなど",
      initialCost: "0円〜",
      monthlyCost: "0円〜数千円",
      salesFee: "5〜7%程度",
      developmentTime: "即日〜数日",
      features: {
        multiDraw: false,
        lastOne: false,
        smsAuth: false,
        ageVerification: false,
        customDomain: true,
        gachaAnimation: false,
      },
      pros: [
        "すぐに始められる",
        "ECとしての信頼性・安定性",
        "決済システムが整っている",
        "初期費用を抑えられる",
      ],
      cons: [
        "オリパ特有の機能がない",
        "ガチャ演出の実装が難しい",
        "手数料が高め",
        "カスタマイズに制限がある",
      ],
      bestFor: "まず小規模で試したい方、ECの基盤を活用したい方",
    },
    {
      name: "独自開発",
      icon: Code,
      type: "フルスクラッチ/受託開発",
      example: "開発会社への依頼、自社開発",
      initialCost: "50万〜300万円以上",
      monthlyCost: "保守費用（月5〜20万円程度）",
      salesFee: "決済手数料のみ（3%程度）",
      developmentTime: "1〜6ヶ月",
      features: {
        multiDraw: true,
        lastOne: true,
        smsAuth: true,
        ageVerification: true,
        customDomain: true,
        gachaAnimation: true,
      },
      pros: [
        "完全なカスタマイズが可能",
        "独自機能を自由に実装",
        "長期的には手数料を抑えられる",
        "ブランディングの自由度が高い",
      ],
      cons: [
        "初期費用が高額",
        "開発期間が長い",
        "保守・運用コストが継続的に発生",
        "技術的な知識・体制が必要",
      ],
      bestFor: "大規模店舗、独自機能が必要な方、長期運用を前提とする方",
    },
    {
      name: "オリパ特化プラットフォーム",
      icon: Layers,
      type: "SaaS型サービス",
      example: "JoyBase、その他オリパ向けサービス",
      initialCost: "0円〜数万円",
      monthlyCost: "0円〜数万円",
      salesFee: "3〜10%程度",
      developmentTime: "即日〜1週間",
      features: {
        multiDraw: true,
        lastOne: true,
        smsAuth: true,
        ageVerification: true,
        customDomain: true,
        gachaAnimation: true,
      },
      pros: [
        "オリパに必要な機能が標準搭載",
        "すぐに始められる",
        "保守・アップデートが不要",
        "初期費用を抑えられる",
      ],
      cons: [
        "カスタマイズに制限がある",
        "プラットフォームに依存する",
        "料金体系はサービスにより様々",
      ],
      bestFor: "オリパ機能を重視する方、手軽に始めたい方",
    },
  ];

  const featureLabels: { [key: string]: string } = {
    multiDraw: "10連/100連ガチャ",
    lastOne: "ラストワン賞",
    smsAuth: "SMS認証",
    ageVerification: "年齢確認",
    customDomain: "独自ドメイン",
    gachaAnimation: "ガチャ演出",
  };

  const selectionPoints = [
    {
      icon: Banknote,
      title: "トータルコストで比較する",
      description:
        "初期費用だけでなく、月額費用・販売手数料・保守費用を含めた総コストで比較しましょう。月商規模によって最適解は変わります。",
    },
    {
      icon: Zap,
      title: "必要な機能を明確にする",
      description:
        "10連ガチャ、ラストワン賞、ガチャ演出など、どの機能が必要かリストアップ。機能が限られていても十分な場合もあります。",
    },
    {
      icon: Shield,
      title: "運用体制を考慮する",
      description:
        "独自開発は自由度が高い反面、運用負荷も大きくなります。自社の技術力・人員体制に合った選択を。",
    },
    {
      icon: Users,
      title: "将来の成長を見据える",
      description:
        "スモールスタートで始めて後から移行するか、最初から本格的に始めるか。移行コストも考慮して判断しましょう。",
    },
  ];

  const faqItems = [
    {
      question: "オリパサイトの構築費用の相場は？",
      answer:
        "ECプラットフォームなら初期費用0円〜、独自開発なら50万〜300万円以上、オリパ特化プラットフォームなら0円〜数万円が一般的です。運用コストも含めて比較することが重要です。",
    },
    {
      question: "どのくらいの期間で始められますか？",
      answer:
        "ECプラットフォームやオリパ特化プラットフォームなら最短即日〜1週間程度。独自開発の場合は1〜6ヶ月程度かかります。ただし、古物商許可証の取得には約40日かかるため、そちらの準備も並行して進めましょう。",
    },
    {
      question: "ECプラットフォームでオリパは運営できる？",
      answer:
        "基本的な販売は可能ですが、10連ガチャやラストワン賞などオリパ特有の機能は自分で実装する必要があります。プラグインやアプリで対応できる場合もありますが、機能は限定的です。",
    },
    {
      question: "途中で別のサービスに移行できますか？",
      answer:
        "技術的には可能ですが、顧客データの移行、URL変更の周知、SEOへの影響など、移行コストがかかります。特に顧客のポイント残高や購入履歴の移行は慎重に行う必要があります。",
    },
  ];

  return (
    <BlogLayout
      title="オリパサイトの作り方｜3つの方法を徹底比較"
      description="ECプラットフォーム、独自開発、オリパ特化プラットフォーム。それぞれのメリット・デメリットをフラットに解説します。"
      category="比較"
      publishedAt="2025年12月1日"
      breadcrumbs={[{ label: "オリパサイト比較" }]}
    >
      <Helmet>
        <title>オリパサイトの作り方｜3つの方法を徹底比較【2025年最新】 | JoyBase</title>
        <meta
          name="description"
          content="オリパサイトを作りたい方必見！ECプラットフォーム、独自開発、オリパ特化プラットフォームの3つの方法を費用・機能・特徴で徹底比較。"
        />
        <meta
          name="keywords"
          content="オリパサイト作り方,オリパサイト構築,オリパ開業,ネットオリパ,オリパシステム,トレカショップ開業"
        />
        <meta property="og:title" content="オリパサイトの作り方｜3つの方法を徹底比較 | JoyBase" />
        <meta
          property="og:description"
          content="ECプラットフォーム、独自開発、オリパ特化プラットフォーム。それぞれのメリット・デメリットをフラットに解説します。"
        />
        <meta property="og:url" content="https://joybase.jp/blog/oripa-site-comparison" />
        <meta property="og:type" content="article" />
        <link rel="canonical" href="https://joybase.jp/blog/oripa-site-comparison" />
      </Helmet>

      {/* 導入 */}
      <section className="mb-12">
        <p>
          オリパ（オリジナルパック）販売を始めたい方にとって、
          <strong>どの方法でサイトを構築するか</strong>は重要な選択です。
          この記事では、オリパサイトを構築する3つの方法を費用・機能・特徴の観点から
          フラットに比較し、あなたに最適な選択肢を見つけるお手伝いをします。
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-6">
          <h3 className="font-bold text-blue-800 mb-2 text-base">この記事でわかること</h3>
          <ul className="text-blue-700 text-sm space-y-1 m-0 list-none p-0">
            <li>✓ オリパサイト構築3つの方法の特徴</li>
            <li>✓ 費用比較（初期費用・月額・手数料）</li>
            <li>✓ 機能比較（10連ガチャ、ラストワン賞、SMS認証など）</li>
            <li>✓ それぞれのメリット・デメリット</li>
          </ul>
        </div>
      </section>

      {/* 3つの方法の概要 */}
      <section className="mb-12">
        <h2>オリパサイトを作る3つの方法</h2>

        <div className="space-y-6 not-prose">
          {comparisonData.map((method, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <method.icon className="w-6 h-6 text-gray-700" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{method.name}</h3>
                  <p className="text-sm text-gray-500">{method.type}</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">例:</span> {method.example}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">こんな方に:</span> {method.bestFor}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 費用比較表 */}
      <section className="mb-12">
        <h2>費用比較</h2>

        <div className="overflow-x-auto not-prose -mx-4 px-4">
          <table className="w-full border-collapse min-w-[600px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  項目
                </th>
                {comparisonData.map((method, index) => (
                  <th
                    key={index}
                    className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                  >
                    {method.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 px-4 py-3 text-sm font-medium text-gray-900">
                  初期費用
                </td>
                {comparisonData.map((method, index) => (
                  <td key={index} className="border border-gray-300 px-4 py-3 text-sm text-gray-600">
                    {method.initialCost}
                  </td>
                ))}
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-4 py-3 text-sm font-medium text-gray-900">
                  月額費用
                </td>
                {comparisonData.map((method, index) => (
                  <td key={index} className="border border-gray-300 px-4 py-3 text-sm text-gray-600">
                    {method.monthlyCost}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-3 text-sm font-medium text-gray-900">
                  販売手数料
                </td>
                {comparisonData.map((method, index) => (
                  <td key={index} className="border border-gray-300 px-4 py-3 text-sm text-gray-600">
                    {method.salesFee}
                  </td>
                ))}
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-4 py-3 text-sm font-medium text-gray-900">
                  開発期間
                </td>
                {comparisonData.map((method, index) => (
                  <td key={index} className="border border-gray-300 px-4 py-3 text-sm text-gray-600">
                    {method.developmentTime}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-sm text-gray-500 mt-4">
          ※ 一般的な相場です。具体的な金額はサービス・業者により異なります。
        </p>
      </section>

      {/* 機能比較 */}
      <section className="mb-12">
        <h2>機能比較</h2>

        <div className="overflow-x-auto not-prose -mx-4 px-4">
          <table className="w-full border-collapse min-w-[600px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  機能
                </th>
                {comparisonData.map((method, index) => (
                  <th
                    key={index}
                    className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold text-gray-900"
                  >
                    {method.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.keys(featureLabels).map((featureKey) => (
                <tr key={featureKey}>
                  <td className="border border-gray-300 px-4 py-3 text-sm font-medium text-gray-900">
                    {featureLabels[featureKey]}
                  </td>
                  {comparisonData.map((method, index) => (
                    <td
                      key={index}
                      className="border border-gray-300 px-4 py-3 text-center"
                    >
                      {method.features[featureKey as keyof typeof method.features] ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-300 mx-auto" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-sm text-gray-500 mt-4">
          ※ 独自開発は追加費用で任意の機能を実装可能。ECプラットフォームもプラグイン等で一部対応できる場合があります。
        </p>
      </section>

      {/* 各方法の詳細 */}
      <section className="mb-12">
        <h2>各方法のメリット・デメリット</h2>

        <div className="space-y-8 not-prose">
          {comparisonData.map((method, index) => (
            <div
              key={index}
              className="bg-gray-50 border border-gray-200 rounded-lg p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
                  <method.icon className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{method.name}</h3>
                  <span className="text-sm text-gray-500">{method.type}</span>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                <span className="font-medium">例:</span> {method.example}
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <h4 className="text-sm font-semibold text-green-700 mb-2">メリット</h4>
                  <ul className="space-y-1">
                    {method.pros.map((pro, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {pro}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-red-700 mb-2">デメリット</h4>
                  <ul className="space-y-1">
                    {method.cons.map((con, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        {con}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">向いている方:</span> {method.bestFor}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 選び方のポイント */}
      <section className="mb-12">
        <h2>選ぶときの4つのポイント</h2>

        <div className="grid md:grid-cols-2 gap-6 not-prose">
          {selectionPoints.map((point, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <point.icon className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900">{point.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{point.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-12">
        <h2>よくある質問</h2>

        <div className="space-y-4 not-prose">
          {faqItems.map((item, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-2">Q: {item.question}</h3>
              <p className="text-gray-600 text-sm m-0">A: {item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* まとめ */}
      <section>
        <h2>まとめ</h2>
        <p>
          オリパサイトの構築方法は、それぞれに長所・短所があります。
          <strong>予算</strong>・<strong>必要な機能</strong>・<strong>運用体制</strong>を
          総合的に考慮して、自分に合った方法を選びましょう。
        </p>

        <div className="bg-gray-100 rounded-lg p-6 mt-6 not-prose">
          <h3 className="font-bold text-gray-900 mb-3">方法選びのまとめ</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <ShoppingCart className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <span><strong>ECプラットフォーム</strong>: まず試したい方、シンプルな販売で十分な方向け</span>
            </li>
            <li className="flex items-start gap-2">
              <Code className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <span><strong>独自開発</strong>: 予算・時間があり、独自機能が必要な大規模店舗向け</span>
            </li>
            <li className="flex items-start gap-2">
              <Layers className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <span><strong>オリパ特化プラットフォーム</strong>: オリパ機能を重視、手軽に始めたい方向け</span>
            </li>
          </ul>
        </div>

        <p className="mt-6">
          どの方法を選んでも、<Link to="/blog/kobutsusho-guide" className="text-blue-600 hover:text-blue-800">古物商許可証</Link>の取得は必要です。
          サイト構築と並行して準備を進めましょう。
        </p>
      </section>
    </BlogLayout>
  );
};

export default OripaSiteComparisonPage;
